import { Link } from '@hotjar/hotsauce';
import { EmptyState } from '@hotjar/webapp/components/EmptyState';
import { useTranslation, Trans } from 'react-i18next';
import { useCurrentSite } from '@hotjar/site';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { SUPPORT_URLS } from '@hotjar/webapp/constants/support';
import {
  HotjarFilterOptionAll,
  HotjarFilterOptionNotWatched,
  HotjarFilterOptionWatched,
  WatchedHotjarFilter,
} from '../../../store/list/types';
import { BottomMarginSubtitle, NoMarginSubtitle, SubtitleContainer, Wrapper } from './styles';

const watchedFiltersOppositeMap = {
  [HotjarFilterOptionNotWatched]: HotjarFilterOptionWatched,
  [HotjarFilterOptionWatched]: HotjarFilterOptionNotWatched,
  [HotjarFilterOptionAll]: HotjarFilterOptionAll,
};

interface Props {
  onButtonClick: () => void;
  onFiltersChangePromptClick: () => void;
  watchedHotjarFilter: WatchedHotjarFilter;
}

export const DefaultEmptyState = ({
  onButtonClick,
  onFiltersChangePromptClick,
  watchedHotjarFilter,
}: Props): JSX.Element => {
  const { data: site } = useCurrentSite();
  const { t } = useTranslation();
  const { click } = useTrack();

  const onLearnMoreClick = () =>
    click('learn_more_link_KB_article_115011624047', 'recordings-filtering-no-matches', {});

  /* Text that prompts the user to change the "watched" filters for the recordings list. */
  const filtersPrompt = (
    <Trans i18nKey="common:recordings.list.empty.suggestion-filters">
      You may want to{' '}
      <Link as="button" kind="inline" onClick={onFiltersChangePromptClick}>
        change your list view
      </Link>{' '}
      to &apos;
      {t(
        `common:recordings.list-actions.watched-recordings.${watchedFiltersOppositeMap[watchedHotjarFilter]}`,
      )}
      &apos;.
    </Trans>
  );

  /* Text that prompts the user to inspect the targeting rules. */
  const targetingPrompt = (
    <Trans i18nKey="common:recordings.list.empty.suggestion-targeting">
      Alternatively, review your{' '}
      <Link as="a" kind="inline" isExternal href={`/site/${site?.id}/settings#targeting`}>
        targeting rules
      </Link>{' '}
      or increase your traffic coverage.
    </Trans>
  );

  const isAllFilterSelected = watchedHotjarFilter === HotjarFilterOptionAll;

  return (
    <Wrapper>
      <EmptyState
        icon="tray_empty"
        iconColor="text_default"
        buttonText={t('common:recordings.list.empty.button')}
        buttonIcon="arrow_up"
        buttonKind="secondary"
        onButtonClick={onButtonClick}
        title={t('common:recordings.list.empty.title')}
        subtitle={
          <SubtitleContainer>
            <BottomMarginSubtitle>
              {t('common:recordings.list.empty.suggestion')}{' '}
              {isAllFilterSelected ? targetingPrompt : filtersPrompt}
            </BottomMarginSubtitle>
            {!isAllFilterSelected && <NoMarginSubtitle>{targetingPrompt}</NoMarginSubtitle>}
          </SubtitleContainer>
        }
      />
      <NoMarginSubtitle>
        <Trans i18nKey="common:recordings.list.empty.tracking">
          Some users can&apos;t be tracked.{' '}
          <Link
            as="a"
            kind="inline"
            onClick={onLearnMoreClick}
            isExternal
            href={SUPPORT_URLS.doesHotjarTrackAllVisitors}
          >
            Learn more
          </Link>
        </Trans>
      </NoMarginSubtitle>
    </Wrapper>
  );
};
